import classNames from "classnames";
import { CheckIcon } from "@heroicons/react/20/solid";
import { Combobox, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState, forwardRef } from "react";
import { ChevronUpDownIcon } from "@heroicons/react/24/outline";

import { debounce } from "../../shared/utils/Global";
import {
  DropdownWithSearchOption,
  DropdownWithSearchParams
} from "../atomic/DropdownWithSearch/interface";
import { whiteLabelStyling } from "../../shared/utils/constants";

function MultipleCheckbox({ selected = false, active = false }) {
  const { whiteLabelStyle } = whiteLabelStyling() as any;

  return (
    <div
      className={classNames(
        "flex items-center justify-center border h-4 w-[21px] rounded-sm",
        selected
          ? `${whiteLabelStyle?.theme?.color?.border || "border-indigo-600"}`
          : "border-gray-200",
        active ? "border-white" : ""
      )}
    >
      {selected ? (
        <span
          className={`w-4 h-4 rounded-sm flex items-center justify-center ${
            active
              ? ""
              : `text-white ${whiteLabelStyle?.theme?.color?.bg || "bg-indigo-600"}`
          }`}
        >
          <CheckIcon className="h-3 w-3" aria-hidden="true" />
        </span>
      ) : null}
    </div>
  );
}

function SingleCheckmark({ selected = false, active = false }) {
  const { whiteLabelStyle } = whiteLabelStyling() as any;

  return (
    <div
      className={classNames(
        "flex items-center justify-center w-5 h-5",
        selected ? "" : "",
        active ? "" : ""
      )}
    >
      {selected ? (
        <span
          className={`w-5 h-5 flex items-center justify-center ${
            active
              ? "text-white"
              : `${whiteLabelStyle?.theme?.color?.text || "text-indigo"} `
          }`}
        >
          <CheckIcon className="h-4 w-4" aria-hidden="true" />
        </span>
      ) : null}
    </div>
  );
}

const DropdownWithSearch = forwardRef<HTMLDivElement, DropdownWithSearchParams>(
  function DropdownWithSearch(
    {
      searchFunc,
      options,
      placeholder,
      multiple,
      onChange,
      styles = {
        container: "",
        label: "",
        dropdown: "",
        button: "",
        chevronIcon: "",
        buttonContainer: ""
      },
      label = "",
      mode = "",
      selectedItems,
      emptyText = "",
      hasSearch = true,
      disabled = false,
      requiredIcon = false,
      hasSelectAll = false,
      inputPlaceholder = "",
      showDropdownIndicator = true,
      errorText = ""
    },
    ref = null
  ) {
    const { whiteLabelStyle } = whiteLabelStyling() as any;

    const [selectAllOption, setSelectAllOptions] = useState(false);
    const [selected, setSelected] = useState<
      DropdownWithSearchOption | DropdownWithSearchOption[]
    >(selectedItems || (multiple ? [] : { name: "", value: "" }));
    const queryRef = useRef<{ value: string } | null>(null);
    const [isSearched, setIsSearched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
      if (!selectedItems || !options?.length) return;

      if (multiple) {
        const resolvedSelected = options.filter(
          (opt) =>
            Array.isArray(selectedItems) &&
            selectedItems.some((item: any) => item.value === opt.value)
        );

        setSelected(resolvedSelected);
      } else {
        const matchedOption = options.find(
          (opt) =>
            !Array.isArray(selectedItems) &&
            opt.value === (selectedItems as any)?.value
        );

        if (matchedOption) {
          setSelected(matchedOption);
        }
      }
    }, [selectedItems, options, multiple]);

    function onOptionSelect(
      data: DropdownWithSearchOption | Array<DropdownWithSearchOption>
    ) {
      setSelected(data);
      onChange(data);
    }

    function comparator(
      a: DropdownWithSearchOption,
      b: DropdownWithSearchOption
    ) {
      return a.value === b.value;
    }

    let multipleButtonText = "";
    if (multiple && Array.isArray(selected)) {
      if (selected.length > 0) {
        multipleButtonText = selected[0].name as string;
      }
      const additionalCount = selected.length > 1 ? selected.length - 1 : 0;
      if (additionalCount) {
        multipleButtonText += " + " + additionalCount + " item(s)";
      }
    }

    const selectAllHandler = (check: boolean) => {
      if (!multiple) return;

      const notSelectedOptions = [] as any;
      options?.forEach((option: any) => {
        const isExist =
          Array.isArray(selected) &&
          selected?.some((item: any) => item?.value === option?.value);
        if (!isExist) {
          notSelectedOptions?.push(option);
        }
      });

      const selectedOptions = check
        ? [...(Array.isArray(selected) ? selected : []), ...notSelectedOptions]
        : [];
      setSelected(selectedOptions);
      onChange?.(selectedOptions);
      setSelectAllOptions(check);
    };

    useEffect(() => {
      if (
        multiple &&
        Array.isArray(selectedItems) &&
        selectedItems?.length === options?.length
      ) {
        setSelectAllOptions(true);
      } else {
        setSelectAllOptions(false);
      }
    }, [selectedItems, options, multiple]);

    const handleSearchChange = debounce(async (...args: unknown[]) => {
      const text = args[0] as string;
      setIsLoading(true);
      await searchFunc?.(text);
      setIsLoading(false);
      if (text.length === 0) {
        setIsSearched(false);
      } else {
        setIsSearched(true);
      }
      if (queryRef.current) {
        queryRef.current.value = text;
      }
    }, 300);

    return (
      <div ref={ref} className={classNames("w-72", styles.container)}>
        {/* @ts-expect-error - Headless UI Combobox has strict overloads that don't work well with conditional multiple prop */}
        <Combobox
          value={selected as any}
          onChange={onOptionSelect}
          {...(multiple ? { multiple: true } : {})}
          by={comparator}
          disabled={disabled}
        >
          <div className="relative">
            {label && (
              <Combobox.Label
                className={classNames(
                  "block font-medium text-gray-700 text-xs mb-2",
                  styles.label
                )}
              >
                {label}{" "}
                {requiredIcon && (
                  <span className="text-inherit text-red-700 ml-1">*</span>
                )}
              </Combobox.Label>
            )}
            <div
              className={classNames(
                "relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left",
                styles.buttonContainer ?? ""
              )}
            >
              <Combobox.Button
                className={classNames(
                  "rounded-lg w-full border flex items-center justify-between py-2 px-4 text-xs text-gray-700",
                  styles.button ?? "",
                  disabled ? "bg-gray-100" : "",
                  errorText !== "" ? "border-red-700" : "border-gray-200"
                )}
                title={
                  multiple && Array.isArray(selected)
                    ? (selected?.length > 0 &&
                        selected.map((s) => s.name).join(",")) ||
                      ""
                    : !Array.isArray(selected)
                      ? (selected.name as string) || ""
                      : ""
                }
                onClick={async () => {
                  if (hasSearch) {
                    setIsLoading(true);
                    await searchFunc?.("");
                    setIsSearched(false);
                    setIsLoading(false);
                  }
                }}
              >
                {multiple ? (
                  Array.isArray(selected) && selected?.length > 0 ? (
                    <ul className="h-full flex pr-10 truncate">
                      {multipleButtonText}
                    </ul>
                  ) : (
                    placeholder
                  )
                ) : !Array.isArray(selected) ? (
                  selected.name || placeholder
                ) : (
                  placeholder
                )}
                {showDropdownIndicator ? (
                  <ChevronUpDownIcon
                    aria-hidden="true"
                    className={classNames(
                      "h-5 w-5 text-gray-400",
                      styles.chevronIcon ?? ""
                    )}
                  />
                ) : null}
              </Combobox.Button>
            </div>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              afterLeave={() => {
                if (queryRef.current) {
                  queryRef.current.value = "";
                }
              }}
            >
              <div
                className={classNames(
                  "absolute mt-1 h-auto w-full shadow-lg bg-white border border-gray-50 rounded-md py-2",
                  styles.dropdown,
                  mode === "reverse" ? "flex flex-col bottom-[40px]" : ""
                )}
                style={{ zIndex: "999" }}
              >
                {hasSearch && (
                  <Combobox.Input
                    tabIndex={0}
                    className={classNames(
                      "block w-[calc(100%_-_16px)] border border-gray-200 mx-2 rounded-lg py-2 px-2 text-xs leading-4 text-gray-900 truncate outline-none mb-1",
                      mode === "reverse" ? "order-2" : "order-1",
                      whiteLabelStyle?.theme?.hover?.focus ||
                        "focus:border-indigo-500",
                      whiteLabelStyle?.theme?.hover?.ring ||
                        "focus:ring-indigo-500"
                    )}
                    displayValue={() => queryRef?.current?.value || ""}
                    onChange={(e) => {
                      e.preventDefault();
                      if (
                        e.target.value?.length > 0 &&
                        /^\s*$/g.test(e.target.value)
                      ) {
                        e.target.value = e.target.value.slice(0, 0);
                        if (queryRef.current) {
                          queryRef.current.value = e.target.value;
                        }
                        return;
                      }

                      handleSearchChange(e.target.value);
                    }}
                    placeholder={inputPlaceholder}
                  />
                )}

                <Combobox.Options
                  className={classNames(
                    "w-full py-1 text-xs focus:outline-none overflow-auto bg-white max-h-[100px] z-[999]",
                    mode === "reverse" ? "order-1" : "order-2"
                  )}
                >
                  {options?.length === 0 ? (
                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                      {isLoading
                        ? null
                        : isSearched
                          ? "No results found"
                          : emptyText}
                    </div>
                  ) : (
                    <>
                      {hasSelectAll && multiple && (
                        <>
                          <label
                            className={classNames(
                              "inline-flex items-center hover:text-white w-full px-2 py-2",
                              whiteLabelStyle?.theme?.hover?.darkbg1 ||
                                "hover:bg-indigo-600"
                            )}
                          >
                            <input
                              type="checkbox"
                              checked={selectAllOption}
                              onChange={(e) =>
                                selectAllHandler(e?.target?.checked)
                              }
                              className={classNames(
                                "form-checkbox h-4 w-4 transition duration-150 ease-in-out border-gray-200 rounded-[2px] ml-2 focus:ring-0 focus:ring-transparent",
                                whiteLabelStyle?.theme?.color?.text ||
                                  "text-indigo-600"
                              )}
                            />
                            <span className="ml-2 text-inherit text-xs">
                              Select All
                            </span>
                          </label>
                        </>
                      )}
                      {options?.map((option, optionIdx) => (
                        <Combobox.Option
                          key={optionIdx}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 px-4 ${
                              active
                                ? `${whiteLabelStyle?.theme?.color?.bg || "bg-indigo-600"} text-white`
                                : "text-gray-900"
                            }`
                          }
                          value={option}
                        >
                          {({ selected, active }) => {
                            return (
                              <div className="flex items-center justify-between w-full">
                                <div className="flex items-center justify-start text">
                                  {multiple && (
                                    <MultipleCheckbox
                                      selected={selected}
                                      active={active}
                                    />
                                  )}
                                  <span
                                    className={classNames(
                                      `block w-full truncate pl-2`,
                                      selected ? "font-normal" : "font-normal"
                                    )}
                                  >
                                    {option.name}
                                  </span>
                                </div>
                                {!multiple && (
                                  <div>
                                    <SingleCheckmark
                                      active={active}
                                      selected={selected}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          }}
                        </Combobox.Option>
                      ))}
                    </>
                  )}
                </Combobox.Options>
              </div>
            </Transition>
          </div>
        </Combobox>

        {errorText && (
          <p className="mt-1 pl-2 text-xs text-red-700">{errorText}</p>
        )}
      </div>
    );
  }
);

export default DropdownWithSearch;
